import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { GuardrailNotice } from "../components/agent/GuardrailNotice";
import { useNavigate, useParams } from "react-router-dom";

import { DocumentPreviewPanel, type PreviewDocument } from "../components/DocumentPreviewPanel";
import { SearchHeader } from "../components/SearchHeader";
import { SearchInput } from "../components/search/SearchInput";
import { AgentMessage } from "../components/agent/AgentMessage";
import { ArtifactPanel } from "../components/agent/ArtifactPanel";
import {
  agentReducer,
  initialAgentState,
  type AgentMessageState,
} from "../components/agent/agentReducer";
import { NoSubscriptionScreen } from "../components/chat/NoSubscriptionScreen";
import { UsageLimitBanner } from "../components/chat/UsageLimitBanner";
import { useUsage } from "../contexts/UsageContext";
import {
  AgentApiError,
  getAgentConversation,
  streamAgentTurn,
} from "../lib/api/agentChat";
import { useRecordsApi } from "../lib/api/records";
import type { EvArtifact } from "../types/agent-chat";

/**
 * The agent chat surface.
 *
 * Deliberately a separate page from ChatPage rather than a mode inside it: the
 * event stream, the message shape and the citation model all differ, and the
 * existing chat must keep working untouched.
 */
/** The most recent user message, used only to locate a guardrail highlight. */
function lastUserMessage(state: { messages: { role: string; text: string }[] }) {
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    if (state.messages[i].role === "user") return state.messages[i].text;
  }
  return undefined;
}

export function AgentChatPage() {
  const navigate = useNavigate();
  const { conversationId: routeConversationId } = useParams<{ conversationId: string }>();
  const { hasSubscription, updateRemainingFromResponse } = useUsage();
  const recordsApi = useRecordsApi();

  const [state, dispatch] = useReducer(agentReducer, initialAgentState);
  const [input, setInput] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<PreviewDocument | null>(null);
  // The generated document shown in the side panel. A new artifact replaces
  // it, so an edited version takes the panel over as soon as it streams in.
  const [activeArtifact, setActiveArtifact] = useState<EvArtifact | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  // Conversation id minted by the server for a turn started on this page. The
  // meta event navigates to /agent/<id>, which must not trigger a history
  // reload: the turn is still streaming and not yet persisted, so the reload
  // would wipe the live messages and the user would have to ask again.
  const ownConversationRef = useRef<string | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const turnSeq = useRef(0);

  // Elapsed-time indicator while a turn runs, matching the legacy page's
  // "Réflexion" affordance.
  useEffect(() => {
    if (!state.isStreaming) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [state.isStreaming]);

  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [state.messages]);

  // Abort any in-flight turn when the page goes away.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Reload a conversation when the route carries an id.
  useEffect(() => {
    let cancelled = false;
    if (routeConversationId && routeConversationId === ownConversationRef.current) {
      return;
    }
    setActiveArtifact(null);
    if (!routeConversationId) {
      ownConversationRef.current = null;
      dispatch({ type: "reset" });
      return;
    }
    ownConversationRef.current = null;
    setIsLoadingHistory(true);
    getAgentConversation(routeConversationId)
      .then((detail) => {
        if (cancelled) return;
        const messages: AgentMessageState[] = detail.messages.map((m) => ({
          key: m.id,
          role: m.sender === "user" ? "user" : "assistant",
          text: m.content,
          steps: [],
          artifacts: (m.artifacts ?? []).map((a) => ({ type: "artifact" as const, ...a })),
          citedIds: m.cited_ids ?? [],
          unusedIds: [],
          isStreaming: false,
          isHistorical: true,
          model: m.model ?? undefined,
        }));
        const citations = detail.messages.flatMap((m) => m.citations ?? []);
        dispatch({ type: "setConversationId", conversationId: detail.id });
        dispatch({ type: "loadHistory", messages, citations });
      })
      .catch((error) => {
        if (cancelled) return;
        dispatch({
          type: "failTurn",
          code: "internal",
          message:
            error instanceof AgentApiError
              ? error.message
              : "Impossible de charger cette conversation.",
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [routeConversationId]);

  const handleDocumentClick = useCallback(async (document: PreviewDocument) => {
    setSelectedDocument({ ...document, content: "Loading content..." });
    try {
      const response = await recordsApi.getDocumentContent(document.id);
      setSelectedDocument((prev) =>
        prev
          ? {
              ...prev,
              content: response.content,
              formatted_content: response.formatted_content,
            }
          : null
      );
    } catch {
      setSelectedDocument((prev) =>
        prev ? { ...prev, content: "Error loading content. Please try again." } : null
      );
    }
  }, [recordsApi]);

  const send = useCallback(
    async (message: string) => {
      const text = message.trim();
      if (!text || state.isStreaming) return;

      setInput("");
      turnSeq.current += 1;
      const seq = turnSeq.current;
      dispatch({ type: "sendUserMessage", text, key: `u-${seq}` });
      dispatch({ type: "beginAssistant", key: `a-${seq}` });

      const conversationId = state.conversationId ?? routeConversationId ?? null;
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        for await (const event of streamAgentTurn(
          // Omitting conversation_id makes the server mint one and return it on
          // the meta event, so a new conversation costs no extra round trip.
          conversationId ? { message: text, conversation_id: conversationId } : { message: text },
          { signal: controller.signal }
        )) {
          dispatch({ type: "event", event });
          if (event.type === "artifact") {
            setActiveArtifact(event);
          }
          if (event.type === "meta" && !conversationId) {
            ownConversationRef.current = event.conversation_id;
            navigate(`/agent/${event.conversation_id}`, { replace: true });
          }
          if (event.type === "done" && event.usage_remaining != null) {
            updateRemainingFromResponse(event.usage_remaining);
          }
        }
      } catch (error) {
        if (controllerAborted(error)) return;
        dispatch({
          type: "failTurn",
          code: error instanceof AgentApiError ? error.code ?? "internal" : "internal",
          message: errorMessage(error),
          retryable: true,
        });
      } finally {
        abortRef.current = null;
      }
    },
    [
      navigate,
      routeConversationId,
      state.conversationId,
      state.isStreaming,
      updateRemainingFromResponse,
    ]
  );

  // The export reads the persisted artifact, so it is offered only once the
  // owning turn has been saved (a done event, or a reloaded conversation).
  const owningMessage = activeArtifact
    ? state.messages.find((m) => m.artifacts.some((a) => a.tool_call_id === activeArtifact.tool_call_id))
    : undefined;
  const canExport =
    !!state.conversationId &&
    !state.isStreaming &&
    !!owningMessage &&
    (!!owningMessage.generationId || !!owningMessage.isHistorical);

  if (!hasSubscription) {
    return (
      <div className="min-h-screen bg-brand-surface">
        <SearchHeader />
        <NoSubscriptionScreen />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-brand-surface">
      <SearchHeader />

      <div className="flex min-h-0 flex-1 pt-16">
      <div
        className={`flex min-h-0 w-full flex-1 flex-col overflow-hidden px-4 pt-4 ${
          activeArtifact ? "min-w-0" : "mx-auto max-w-3xl"
        }`}
      >
        <div ref={messagesRef} className="scrollbar-hidden flex-1 overflow-y-auto py-4">
          {isLoadingHistory && (
            <p className="py-8 text-center text-brand-muted">Chargement de la conversation…</p>
          )}

          {!isLoadingHistory && state.messages.length === 0 && (
            <div className="py-16 text-center">
              <h1 className="mb-2 text-2xl font-bold text-brand-text">Assistant</h1>
              <p className="text-brand-muted">
                Posez une question sur vos documents RH, ou demandez une description de poste
                ou une échelle salariale.
              </p>
            </div>
          )}

          {state.messages.map((message) => (
            <AgentMessage
              key={message.key}
              message={message}
              citations={state.citations}
              onOpenArtifact={setActiveArtifact}
              activeArtifactId={activeArtifact?.tool_call_id ?? null}
              onOpenDocument={(citation) =>
                handleDocumentClick({
                  id:
                    citation.locator.type === "char_range"
                      ? citation.locator.document_id
                      : citation.id,
                  title: citation.title,
                  score: citation.score ?? undefined,
                })
              }
            />
          ))}

          {state.isStreaming && (
            <p className="mb-4 text-sm text-brand-muted">Réflexion — {elapsed}s</p>
          )}

          {state.guardrail && (
            <GuardrailNotice
              guardrail={state.guardrail}
              message={lastUserMessage(state)}
            />
          )}
          {/* A blocked turn already explains itself through the guardrail
              notice; repeating it as a generic error would say it twice. */}
          {state.error && state.error.code !== "pii_blocked" && (
            <div className="mb-4 rounded-lg bg-brand-error/10 px-4 py-3 text-sm text-brand-error">
              {state.error.message}
            </div>
          )}

          <DocumentPreviewPanel
            document={selectedDocument}
            onClose={() => setSelectedDocument(null)}
            onRetry={handleDocumentClick}
          />
        </div>

        <div className="pb-6 pt-3">
          <UsageLimitBanner />
          <SearchInput
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onSearch={send}
            disabled={state.isStreaming}
          />
          <p className="mt-2 text-center text-[11px] text-brand-muted">
            L'assistant peut faire des erreurs. Vérifiez les sources citées.
          </p>
        </div>
      </div>

      {activeArtifact && (
        <div className="fixed inset-0 z-40 pt-16 lg:static lg:z-auto lg:w-[46%] lg:max-w-[760px] lg:flex-shrink-0 lg:pt-0">
          <ArtifactPanel
            artifact={activeArtifact}
            citations={state.citations}
            conversationId={state.conversationId}
            canExport={canExport}
            onClose={() => setActiveArtifact(null)}
            onOpenDocument={(citation) =>
              handleDocumentClick({
                id:
                  citation.locator.type === "char_range"
                    ? citation.locator.document_id
                    : citation.id,
                title: citation.title,
                score: citation.score ?? undefined,
              })
            }
          />
        </div>
      )}
      </div>
    </div>
  );
}

function controllerAborted(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function errorMessage(error: unknown): string {
  if (error instanceof AgentApiError) return error.message;
  return "Une erreur technique est survenue. Réessayez.";
}
